import React from 'react';
import { 
  FileText, 
  Users, 
  Activity, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  User
} from 'lucide-react';
import { UserDashboardStatsDto, StatusDto } from '../../types';

interface UserStatsCardsProps {
  stats: UserDashboardStatsDto;
  isLoading?: boolean;
  activeFilter?: string;
  onStatClick?: (filter: string) => void;
  tabCounts?: Record<string, number>;
  statuses?: StatusDto[];
  workOrders?: any[];
  userId?: number;
}

const UserStatsCards: React.FC<UserStatsCardsProps> = ({
  stats,
  isLoading,
  activeFilter,
  onStatClick,
  tabCounts,
  statuses = [],
  workOrders = [],
  userId
}) => {
  const tabGroupCounts = React.useMemo(() => {
    if (tabCounts) {
      return tabCounts;
    }

    const counts: Record<string, number> = {
      'unassigned': 0,
      'my-open': 0,
      'group:New Work': 0,
      'group:Active Work': 0,
      'group:On Hold': 0,
      'group:Pending': 0,
      'group:Complete': 0,
      'group:Closed': 0
    };
    
    if (!workOrders?.length || !statuses?.length) return counts;
    
    // Create status ID to TabGroup mapping
    const statusIdToTabGroup = new Map<number, string>();
    statuses.forEach(status => {
      statusIdToTabGroup.set(status.id, (status as any).tabGroup || 'Active Work');
    });
    
    // Count work orders by TabGroup and user assignment
    workOrders.forEach(wo => {
      const tabGroup = statusIdToTabGroup.get(wo.statusId || 0) || 'Active Work';
      const isUserAssigned = wo.assignedUserId === userId;
      const isOpenStatus = wo.statusName && !['Completed', 'Closed', 'Cancelled'].includes(wo.statusName);
      
      // Count unassigned
      if (!wo.assignedUserName) {
        counts['unassigned']++;
      }
      
      // Count my-open (user's assigned open work orders)
      if (isUserAssigned && isOpenStatus) {
        counts['my-open']++;
      }
      
      // Count by TabGroup
      counts[`group:${tabGroup}`] = (counts[`group:${tabGroup}`] || 0) + 1;
    });
    
    return counts;
  }, [tabCounts, workOrders, statuses, userId]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[...Array(8)].map((_, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 animate-pulse">
            <div className="flex flex-col items-center text-center">
              <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full mb-2"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-12 mb-1"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: 'My Work Orders',
      value: tabGroupCounts['my-open'],
      icon: <FileText className="h-6 w-6 text-green-600" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      description: 'My assignments',
      filterKey: 'my-open',
      clickable: true
    },
    {
      title: 'New Work',
      value: tabGroupCounts['group:New Work'],
      icon: <FileText className="h-6 w-6 text-blue-600" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'New work',
      filterKey: 'group:New Work',
      clickable: true
    },
    {
      title: 'Unassigned',
      value: tabGroupCounts['unassigned'],
      icon: <Users className="h-6 w-6 text-orange-600" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      description: 'No assignee',
      filterKey: 'unassigned',
      clickable: true
    },
    {
      title: 'Active Work',
      value: tabGroupCounts['group:Active Work'],
      icon: <Activity className="h-6 w-6 text-yellow-600" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      description: 'In progress',
      filterKey: 'group:Active Work',
      clickable: true
    },
    {
      title: 'On Hold',
      value: tabGroupCounts['group:On Hold'],
      icon: <Clock className="h-6 w-6 text-gray-600" />,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      description: 'Paused',
      filterKey: 'group:On Hold',
      clickable: true
    },
    {
      title: 'Pending',
      value: tabGroupCounts['group:Pending'],
      icon: <User className="h-6 w-6 text-purple-600" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: 'Awaiting',
      filterKey: 'group:Pending',
      clickable: true
    },
    {
      title: 'Complete',
      value: tabGroupCounts['group:Complete'],
      icon: <CheckCircle className="h-6 w-6 text-green-600" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      description: 'Finished',
      filterKey: 'group:Complete',
      clickable: true
    },
    {
      title: 'Closed',
      value: tabGroupCounts['group:Closed'],
      icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      description: 'Archived',
      filterKey: 'group:Closed',
      clickable: true
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
      {cards.map((card, index) => {
        const isActive = activeFilter === card.filterKey;
        return (
          <div 
            key={index} 
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 transition-all duration-200 ${
              card.clickable 
                ? 'hover:shadow-md cursor-pointer hover:border-accent-color-300 hover:scale-[1.05]' 
                : 'hover:shadow-md'
            } ${
              isActive 
                ? 'border-accent-color-500 border-2 shadow-lg transform scale-[1.02]' 
                : ''
            }`}
            onClick={() => {
              if (card.clickable) {
                onStatClick?.(card.filterKey!);
              }
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div className={`p-2 rounded-full ${card.bgColor} mb-2`}>
                {card.icon}
              </div>
              <p className={`text-lg font-bold ${card.color} mb-1`}>{card.value}</p>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight">{card.title}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UserStatsCards;
